import type { IHealthRepository } from "@src/domain/system/repository/health_repository";
import { Health } from "@src/domain/system/value_objects/health";
import type { ServiceHealth, DatabasePool } from "@src/domain/system/value_objects/health";
import type { ApiClient } from "../api/client";

/**
 * Health Repository Implementation
 * Implements the IHealthRepository interface using HTTP API calls
 */
export class HealthRepositoryImpl implements IHealthRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async getHealth(): Promise<Health> {
        const data = await this.apiClient.get<any>("/health");

        // Transform services object to the expected format
        const services: Record<string, ServiceHealth> = {};

        if (data.services) {
            for (const [serviceName, serviceStatus] of Object.entries(data.services)) {
                const statusString = serviceStatus as string;

                // Parse the status - it might be "healthy", "unhealthy", or "unhealthy: error message"
                let status: "healthy" | "unhealthy" = "healthy";
                let message: string | undefined;

                if (statusString.startsWith("unhealthy")) {
                    status = "unhealthy";
                    // Extract error message if present (e.g., "unhealthy: __aenter__")
                    const parts = statusString.split(":");
                    if (parts.length > 1) {
                        message = parts.slice(1).join(":").trim();
                    }
                } else if (statusString === "healthy") {
                    status = "healthy";
                } else {
                    // If status is neither "healthy" nor starts with "unhealthy", treat as unhealthy
                    status = "unhealthy";
                    message = statusString;
                }

                services[serviceName] = {
                    name: serviceName,
                    status,
                    message,
                };
            }
        }

        // Map database_pool if present
        let databasePool: DatabasePool | undefined;
        if (data.database_pool) {
            databasePool = {
                statistics: {
                    size: data.database_pool.statistics.size,
                    checked_out: data.database_pool.statistics.checked_out,
                    overflow: data.database_pool.statistics.overflow,
                    checked_in: data.database_pool.statistics.checked_in,
                    total: data.database_pool.statistics.total,
                },
                utilization_percent: data.database_pool.utilization_percent,
                status: data.database_pool.status === "healthy" ? "healthy" : "unhealthy",
            };
        }

        return Health.create({
            status: data.status,
            timestamp: new Date(data.timestamp),
            services,
            database_pool: databasePool,
        });
    }
}
